import { Link } from "react-router-dom";
import { adminApi, ApiError } from "../../lib/api";
import {
  formatInvoicePaymentDeadline,
  formatMoscowDateTime,
  formatPaymentRub,
} from "../../lib/format";
import { usePaymentDocuments } from "../../lib/payment-documents";
import {
  invoiceStatusLabels,
  paymentButton,
  usePaymentScope,
  usePaymentWrite,
} from "../../lib/payment-ui";
import { PaymentApplicationPanel } from "./payment-application-panel";
import type { InvoiceActionProps } from "./payment-application-panel";
import {
  PaymentReviewPanel,
  PaymentCorrectionForm,
} from "./payment-review-panel";
import { PaymentReceipts } from "./payment-receipts";

export function PaymentInvoicePanel({
  accountId,
  invoice,
  onChanged,
}: InvoiceActionProps) {
  const scope = usePaymentScope(accountId),
    docs = usePaymentDocuments(accountId),
    write = usePaymentWrite(accountId);
  async function retry() {
    if (!scope.token || write.locked) return;
    const token = scope.token;
    await write.run(
      { requestId: crypto.randomUUID(), expectedVersion: invoice.version },
      (body) => adminApi.retryInvoice(token, accountId, invoice.id, body),
      () => onChanged(),
      async (caught) => {
        if (caught instanceof ApiError && caught.status === 409)
          await onChanged();
      },
    );
  }
  function pdf(open: boolean) {
    if (!scope.token) return;
    const token = scope.token;
    const guard = scope.capture();
    void docs.fetchDocument(
      async () => {
        try {
          return await adminApi.invoicePdf(
            token,
            accountId,
            invoice.id,
            open ? "inline" : "attachment",
          );
        } catch (caught) {
          if (
            guard.isCurrent() &&
            caught instanceof ApiError &&
            caught.status === 409 &&
            caught.code === "PAYMENT_DOCUMENT_NOT_READY"
          ) {
            await onChanged();
          }
          throw caught;
        }
      },
      `invoice-${invoice.number}.pdf`,
      open,
    );
  }
  const application = invoice.application;
  const needsPaymentConfirmation = invoice.status === "PAYMENT_REPORTED";
  return (
    <article
      className={`min-w-0 space-y-4 ${needsPaymentConfirmation ? "rounded-md border-2 border-primary p-3" : ""}`}
      aria-label={`Счёт ${invoice.number}`}
    >
      <header className="space-y-2">
        <h2 className="text-xl font-semibold">Счёт № {invoice.number}</h2>
        <p className="break-words">{invoice.description}</p>
        <p className="font-medium">
          {invoiceStatusLabels[invoice.status]} ·{" "}
          {formatPaymentRub(invoice.totalRub)}
        </p>
        <p className="text-sm">
          Выдан {formatMoscowDateTime(invoice.issuedAt)} МСК; оплатить{" "}
          {formatInvoicePaymentDeadline(invoice.expiresAt)}.
        </p>
        {invoice.paymentConfirmedAt && (
          <p className="text-sm">
            Оплата подтверждена{" "}
            {formatMoscowDateTime(invoice.paymentConfirmedAt)} МСК.
          </p>
        )}
        {invoice.paymentReportedAt && (
          <p className="text-sm">
            Клиент заявил об оплате {formatMoscowDateTime(invoice.paymentReportedAt)} МСК.
            Заявление не подтверждает поступление денег.
          </p>
        )}
        {invoice.activationDueAt && !application && (
          <p className="text-sm">
            Применить не позднее {formatMoscowDateTime(invoice.activationDueAt)}{" "}
            МСК.
          </p>
        )}
        {invoice.supersededBy && (
          <Link
            className="text-primary underline"
            to={`/accounts/${accountId}/payments?invoice=${encodeURIComponent(invoice.supersededBy.id)}`}
          >
            Новый счёт № {invoice.supersededBy.number}
          </Link>
        )}
        {invoice.document.state === "WRITING" && (
          <p role="status">PDF формируется</p>
        )}
        {invoice.document.state === "FAILED" && (
          <p role="alert">
            Не удалось сформировать PDF ({invoice.document.errorCode}). Номер и
            срок счёта сохранены.
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          {invoice.document.state === "READY" && (
            <>
              <button
                className={paymentButton}
                disabled={docs.busy}
                onClick={() => pdf(true)}
              >
                Открыть PDF
              </button>
              <button
                className={paymentButton}
                disabled={docs.busy}
                onClick={() => pdf(false)}
              >
                Скачать счёт
              </button>
            </>
          )}
          {invoice.allowedCommands.retryDocument && !write.uncertain && (
            <button
              className={paymentButton}
              disabled={write.locked}
              onClick={() => void retry()}
            >
              Повторить формирование
            </button>
          )}
          {write.uncertain && (
            <button
              className={paymentButton}
              disabled={write.pending}
              onClick={() => void write.retry()}
            >
              Повторить запрос формирования
            </button>
          )}
          <Link className={paymentButton} to={`/accounts/${accountId}/billing`}>
            Открыть подписку
          </Link>
        </div>
        {(docs.error || write.error) && (
          <p role="alert" className="text-sm text-danger">
            {docs.error ?? write.error}
          </p>
        )}
      </header>
      <dl className="grid gap-3 rounded-md border p-3 text-sm sm:grid-cols-3">
        <div>
          <dt>Поступило</dt>
          <dd className="break-all tabular-nums">
            {formatPaymentRub(invoice.receivedRub)}
          </dd>
        </div>
        <div>
          <dt>Зачтено</dt>
          <dd className="break-all tabular-nums">
            {formatPaymentRub(invoice.allocatedRub)}
          </dd>
        </div>
        <div>
          <dt>Не зачтено</dt>
          <dd className="break-all tabular-nums">
            {formatPaymentRub(invoice.unallocatedRub)}
          </dd>
        </div>
      </dl>
      {application && (
        <section
          className="space-y-2 rounded-md border p-3 text-sm"
          id={`operation-${application.billingOperationId}`}
        >
          <h3 className="font-semibold">Применённая операция</h3>
          <p>Покупка уже применена.</p>
          <p className="break-all">
            ID: <code>{application.billingOperationId}</code>
          </p>
          <p>
            Применена {formatMoscowDateTime(application.appliedAt)}; эффект с{" "}
            {formatMoscowDateTime(application.effectiveAt)} МСК.
          </p>
          {invoice.action.kind === "RENEW" && (
            <p>
              Продление назначено с{" "}
              {formatMoscowDateTime(application.accessStartsAt)} МСК.
            </p>
          )}
          <p>
            Месячный объём: {application.monthlyDialogs}; докупка:{" "}
            {application.topupDialogs}; оплачено до{" "}
            {formatMoscowDateTime(application.paidThroughAt)}.
          </p>
          <a
            className="text-primary underline"
            href={`#operation-${application.billingOperationId}`}
          >
            Ссылка на операцию
          </a>
        </section>
      )}
      <details className="rounded-md border p-3">
        <summary className="cursor-pointer font-medium">
          Зафиксированные реквизиты и условия
        </summary>
        <div className="mt-3 grid gap-4 text-sm sm:grid-cols-2">
          <section className="space-y-1 break-words">
            <h3 className="font-semibold">Плательщик</h3>
            <p>{invoice.payer.name}</p>
            <p>
              ИНН {invoice.payer.inn}
              {invoice.payer.kpp ? `, КПП ${invoice.payer.kpp}` : ""}
            </p>
            <p>{invoice.payer.address}</p>
            {invoice.payer.email && <p>{invoice.payer.email}</p>}
          </section>
          <section className="space-y-1 break-words">
            <h3 className="font-semibold">Получатель · НПД</h3>
            <p>
              {invoice.recipient.name} · ИНН {invoice.recipient.inn}
            </p>
            <p>{invoice.recipient.address}</p>
            <p>{invoice.recipient.email}</p>
            <p>
              {invoice.recipient.bankName} · БИК {invoice.recipient.bik}
            </p>
            <p className="break-all">
              Счёт {invoice.recipient.bankAccount}; корсчёт{" "}
              {invoice.recipient.correspondentAccount}
            </p>
          </section>
          <section className="space-y-1 break-words sm:col-span-2">
            <h3 className="font-semibold">Условия покупки</h3>
            <p>
              {invoice.purchase.targetSnapshot.tariffName};{" "}
              {invoice.purchase.targetSnapshot.monthlyDialogs} диалогов / 30
              дней; срок исходной покупки{" "}
              {invoice.purchase.targetSnapshot.months} × 30 дней.
            </p>
            <p>
              Расчёт {formatMoscowDateTime(invoice.purchase.calculatedAt)} МСК.
              Цена сохранена: {formatPaymentRub(invoice.purchase.totalRub)}.
            </p>
            <p>
              Период: {formatMoscowDateTime(invoice.purchase.periodStartsAt)} —{" "}
              {formatMoscowDateTime(invoice.purchase.periodEndsAt)}.
            </p>
            <p>
              Без скидки:{" "}
              {invoice.purchase.breakdown.undiscountedRub ?? "не применяется"};
              скидка:{" "}
              {invoice.purchase.breakdown.discountRub ?? "не применяется"} ₽.
            </p>
            <a
              className="text-primary underline"
              href={invoice.offer.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {invoice.offer.title} от {invoice.offer.editionDate}
            </a>
          </section>
        </div>
      </details>
      <PaymentApplicationPanel
        accountId={accountId}
        invoice={invoice}
        onChanged={onChanged}
      />
      <PaymentReviewPanel
        accountId={accountId}
        invoice={invoice}
        onChanged={onChanged}
      />
      <section className="space-y-3">
        <h3 className="font-semibold">Банковские поступления</h3>
        {invoice.payments.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Поступления ещё не зарегистрированы.
          </p>
        )}
        {invoice.payments.map((payment) => (
          <section
            key={payment.id}
            className="space-y-3 rounded-md border p-3 text-sm"
            aria-label={`Перевод ${payment.id}`}
          >
            <h4 className="break-all font-medium">
              {formatPaymentRub(payment.amountRub)} · {payment.payerName}
            </h4>
            <p className="break-all">ID {payment.id}</p>
            <p>
              В банке: {formatMoscowDateTime(payment.receivedAt)} МСК; записан:{" "}
              {formatMoscowDateTime(payment.confirmedAt)} МСК.
            </p>
            <p className="break-all">
              {payment.bankIdentity.kind === "BANK_OPERATION"
                ? `Банковская операция ${payment.bankIdentity.operationId}`
                : `Выписка ${payment.bankIdentity.statementId}, строка ${payment.bankIdentity.rowId}`}
            </p>
            <p>
              Зачтено {formatPaymentRub(payment.allocatedRub)}; не зачтено{" "}
              {formatPaymentRub(payment.unallocatedRub)}.
            </p>
            {payment.recordState === "ERROR" && (
              <p className="font-medium">Ошибочная запись</p>
            )}
            {payment.reviewRequired && <p>Требует проверки</p>}
            {payment.correctsPaymentId && (
              <p className="break-all">
                Исправляет {payment.correctsPaymentId}
              </p>
            )}
            {payment.note && (
              <p className="whitespace-pre-wrap break-words">{payment.note}</p>
            )}
            <PaymentCorrectionForm
              accountId={accountId}
              payment={payment}
              onChanged={onChanged}
            />
            <PaymentReceipts
              accountId={accountId}
              payment={payment}
              onChanged={onChanged}
            />
          </section>
        ))}
      </section>
    </article>
  );
}
